/**
 * Реализация `fixtures`: файлы прототипа, отданные статикой.
 *
 * Файлы НЕ копируются в git второй раз: `prototype/data` раздаётся плагином
 * `proto-data` (`vite.config.ts`) по адресу `/data/...` — в dev через
 * middleware, в сборке копированием в `dist/data`. Тот же контракт адресов,
 * что и у прототипа, поэтому загрузчик прототипа (`data/loader.ts`) переехал
 * дословно и здесь только обёрнут в общий интерфейс.
 */

import { DATA_ROOT, STUB_DIR, TRACK_DIR, loadJson } from '../data/loader';
import type { TrackId } from '../data/types';
import type { DataSource, ReadResult, SourcePrincipal } from './types';
import { defineMessages } from '../../i18n/define';
import { tr } from '../../i18n/runtime';

const m = defineMessages({
  trackFailed: {
    id: 'data.fixtures.trackFailed',
    defaultMessage: 'трек {track}: {reason}',
    description: 'Причина пустого блока на демонстрационных данных; {track} — буква набора данных (A или B), {reason} — готовая причина («файл пуст»)',
  },
});

/**
 * Наборы, общие для треков и потому лежащие в КОРНЕ фикстур, а не в папке
 * трека. Шаблоны агроциклов приходят из выжимки агрономов: они не зависят от
 * случайного зерна генератора, и копия в каждом треке была бы второй правдой о
 * том же документе. Список именно список — чтобы «этот набор общий» нельзя
 * было сказать про любой файл молча.
 */
const ROOT_SETS: readonly string[] = ['templates.json'];

export function createFixturesSource(track: TrackId): DataSource {
  return {
    kind: 'fixtures',

    async read<T>(name: string): Promise<ReadResult<T>> {
      const dir = ROOT_SETS.includes(name) ? '' : `${TRACK_DIR[track]}/`;
      const primary = await loadJson<T>(`${DATA_ROOT}/${dir}${name}`);
      if (primary.ok) return primary;
      // Стаб-трек — страховка каркаса, пока генератор не отработал: экран
      // остаётся проходимым, а причина первой неудачи не теряется.
      const stub = await loadJson<T>(`${DATA_ROOT}/${STUB_DIR}/${name}`);
      if (stub.ok) return stub;
      return { ok: false, reason: tr(m.trackFailed, { track, reason: primary.reason }) };
    },

    /**
     * Фикстуры про вход ничего не знают: кто вошёл — вопрос сервера, и
     * отвечать на него набором файлов значило бы завести вторую правду о
     * правах рядом с `/auth/me`.
     */
    async principal(): Promise<SourcePrincipal | null> {
      return null;
    },
  };
}
